import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import { format } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { DayModifiers } from 'react-day-picker';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import { DayPicker } from '../../../../../design-system/Form/DayPicker';
import { DossierId } from '../../../../../resources/dossier/types';
import { useUploadVouchersMutation } from '../../../../../resources/dossier/mutations';

import { ModalAllowed } from '../Vouchers';

import s from './DeferAgreement.module.css';

type Props = {
    dossierId: DossierId;
    handleModal: (value: ModalAllowed | false) => void;
    initialDate: Date;
    isOpen: boolean;
    modal?: typeof DEFER_DATE_PICKER | typeof UPLOAD_VOUCHER;
    onClose: () => void;
};

export const DEFER_DATE_PICKER = 'defer-date-picker';
export const UPLOAD_VOUCHER = 'upload-voucher';

export const DeferModal = ({
    dossierId,
    handleModal,
    isOpen,
    modal = DEFER_DATE_PICKER,
    onClose,
    initialDate,
}: Props): JSX.Element => {
    const intl = useIntl();
    const uploadVouchersMutation = useUploadVouchersMutation(dossierId);
    const [modalToDisplay, setModalToDisplay] = useState<
        typeof DEFER_DATE_PICKER | typeof UPLOAD_VOUCHER
    >(modal);
    const [deferDate, setDeferDate] = useState<Date>(initialDate);
    const [vouchers, addVouchers] = useState<File[]>([]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const uniqueVouchers = [
                ...new Map(
                    vouchers.concat(acceptedFiles).map((voucher) => [voucher.name, voucher]),
                ).values(),
            ];
            addVouchers(uniqueVouchers);
        },
        [vouchers],
    );

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
        },
        useFsAccessApi: false,
        onDrop,
    });

    const handleDayPickerClick = (day: Date, modifiers: DayModifiers): void => {
        if (!modifiers.disabled) {
            setDeferDate(day);
        }
    };

    const uploadVouchersAndDeferTrip = (): void => {
        uploadVouchersMutation.mutate({ vouchers, deferDate: format(deferDate, 'yyyy-MM-dd') });
        handleModal(false);
    };

    const removeVoucher = (selectedVoucher: File): void => {
        addVouchers(vouchers.filter((voucher) => selectedVoucher !== voucher));
    };

    return (
        <React.Fragment>
            {modalToDisplay === DEFER_DATE_PICKER && (
                <Modal
                    isOpen={isOpen}
                    title={intl.formatMessage({
                        id: 'dossier.deferred.request',
                    })}
                    mainButton={
                        <Button
                            priority="primary"
                            onClick={() => setModalToDisplay(UPLOAD_VOUCHER)}
                        >
                            <FormattedMessage id="form.submit" />
                        </Button>
                    }
                    secondaryButton={
                        <Button priority="secondary" onClick={onClose}>
                            <FormattedMessage id="form.cancel" />
                        </Button>
                    }
                >
                    <p>
                        <FormattedMessage id="dossier.deferred.message" />
                    </p>

                    <DayPicker
                        selected={deferDate}
                        onDayClick={handleDayPickerClick}
                        fromDate={initialDate}
                        defaultMonth={initialDate}
                    />
                </Modal>
            )}
            {modalToDisplay === UPLOAD_VOUCHER && (
                <Modal
                    isOpen={isOpen}
                    title={intl.formatMessage({
                        id: 'dossier.upload_voucher.request',
                    })}
                    mainButton={
                        <Button
                            priority="primary"
                            onClick={uploadVouchersAndDeferTrip}
                            disabled={vouchers.length === 0}
                        >
                            <FormattedMessage id="form.submit" />
                        </Button>
                    }
                    secondaryButton={
                        <Button priority="secondary" onClick={onClose}>
                            <FormattedMessage id="form.cancel" />
                        </Button>
                    }
                >
                    <p>
                        <FormattedMessage id="dossier.upload_voucher.message" />
                    </p>
                    <div {...getRootProps()}>
                        <input id="vouchers" {...getInputProps()} />
                        <Button priority="secondary">
                            <label htmlFor="vouchers" onClick={(e) => e.preventDefault()}>
                                <FormattedMessage id="dossier.upload_voucher.add" />
                            </label>
                        </Button>
                    </div>
                    <ul className={s.vouchers}>
                        {vouchers.map((voucher) => (
                            <li className="badge" key={voucher.name}>
                                <button type="button" onClick={() => removeVoucher(voucher)}>
                                    X
                                </button>
                                {voucher.name}
                            </li>
                        ))}
                    </ul>
                </Modal>
            )}
        </React.Fragment>
    );
};
